import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  EnergyPlusTestbedService,
  EnergyPlusTestbedStatus,
} from '../services/EnergyPlusTestbedService';
import {
  markTrackingRunAccepted,
  markTrackingRunFailed,
  markTrackingRunRequested,
} from '../services/TrackingRunSession';
import { HistoricalService } from '../services/HistoricalService';
import { setPreferredDataSourceMode } from '../services/DataSourcePreference';
import { AuthService } from '../services/AuthService';

const format = (value: number | undefined, digits = 1) => (
  Number.isFinite(value) ? Number(value).toFixed(digits) : '--'
);

const SPEED_OPTIONS = [
  { seconds: 5, label: '5초', recommended: true },
  { seconds: 10, label: '10초' },
  { seconds: 30, label: '30초' },
  { seconds: 60, label: '1분' },
  { seconds: 180, label: '3분' },
] as const;

const visibleDemoDelay = (seconds: number | undefined) => (
  Number.isFinite(seconds) && Number(seconds) >= 1 ? Number(seconds) : 5
);

const durationLabel = (seconds: number | undefined) => {
  const value = Number(seconds);
  if (!Number.isFinite(value)) return '--';
  if (value < 60) return `${value.toFixed(value < 1 ? 2 : 0)}초`;
  return `${(value / 60).toFixed(value % 60 === 0 ? 0 : 1)}분`;
};

const AUTOMATIC_CALIBRATION_CANDIDATE_COUNT = 21;
const AUTOMATIC_CALIBRATION_ALIGNMENT_TOLERANCE_SEC = 1200;
const ENERGYPLUS_GUIDE_ACTION_KEY = 'aees:energyplus-guide-action';

type TestRunMode = 'demo' | 'calibration';
type EnergyPlusGuideAction = { id: string; mode: TestRunMode; requestedAt: number };

const scheduleIndexFromFrameTime = (time: string | undefined) => {
  if (!time) return null;
  const [hours = 0, minutes = 0, seconds = 0] = time.split(':').map(Number);
  if (![hours, minutes, seconds].every(Number.isFinite)) return null;
  return Math.floor(((hours * 3600) + (minutes * 60) + seconds) / 1200);
};

const componentScheduleForFrame = (time: string | undefined, targetActive: boolean) => {
  const targetIndex = scheduleIndexFromFrameTime(time);
  return Array.from({ length: 72 }, (_, index) => {
    const seconds = index * 1200;
    const normallyScheduled = seconds >= 9 * 3600 && seconds < 18 * 3600;
    return index === targetIndex ? (targetActive ? 1 : 0) : (normallyScheduled ? 1 : 0);
  });
};

const averageFinite = (values: unknown[]) => {
  const finite = values
    .map(Number)
    .filter(value => Number.isFinite(value));
  return finite.length ? finite.reduce((sum, value) => sum + value, 0) / finite.length : null;
};

interface EnergyPlusTestbedBarProps {
  activeTab?: string;
}

export const EnergyPlusTestbedBar: React.FC<EnergyPlusTestbedBarProps> = ({ activeTab }) => {
  const [status, setStatus] = useState<EnergyPlusTestbedStatus>(() => EnergyPlusTestbedService.getStatus());
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [minimized, setMinimized] = useState(() => {
    try {
      return window.localStorage.getItem('aees:energyplus-panel-minimized') === '1';
    } catch {
      return false;
    }
  });
  const [reportPanelExpanded, setReportPanelExpanded] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [controlError, setControlError] = useState<string | null>(null);
  const [automaticCalibrationError, setAutomaticCalibrationError] = useState<string | null>(null);
  const [activeRunMode, setActiveRunMode] = useState<TestRunMode>('demo');
  const [calibrationPackageMessage, setCalibrationPackageMessage] = useState<string | null>(null);
  const [calibrationPackageError, setCalibrationPackageError] = useState<string | null>(null);
  const automaticCalibrationRunRef = useRef<string | null>(null);
  const automaticCalibrationRequestInFlightRef = useRef(false);
  const handledGuideActionRef = useRef<string | null>(null);
  const packagedTrackingRunRef = useRef<string | null>(null);
  const [trackingMonitor, setTrackingMonitor] = useState<{
    running: boolean;
    calibrationRunning: boolean;
    rlSetpointsLinked: boolean;
    requiresProcessRestart: boolean;
    outputStale: boolean;
    waitingForFeed: boolean;
    outputCount: number;
    controlCallbackCount: number;
    lastOutputTimestamp: number | null;
    runKey: string | null;
    batchRunning: boolean;
    batchPhase: string;
    batchError: string | null;
    trackingMode: string;
    phase: string;
  } | null>(null);

  useEffect(() => EnergyPlusTestbedService.subscribe(setStatus), []);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch('/api/twin/tracking/status', { cache: 'no-store' });
        if (!response.ok) throw new Error('Tracking status unavailable');
        const payload = await response.json();
        const setpointStatus = payload?.setpoint_status ?? {};
        const rlSetpointsLinked = ['ahu_sa_temp', 'chw_sp', 'cw_sp', 'bt4_sp']
          .every(key => setpointStatus?.[key]?.source === 'rl_policy_action');
        if (!cancelled) {
          const artifactRunKey = payload?.diagnostics?.artifact_history?.run_key;
          const startedAt = payload?.diagnostics?.started_at_wall;
          setTrackingMonitor({
            running: Boolean(payload?.running),
            calibrationRunning: Boolean(payload?.calibration_status?.running),
            rlSetpointsLinked,
            requiresProcessRestart: Boolean(payload?.requires_process_restart),
            outputStale: Boolean(payload?.tracking_output_stale),
            waitingForFeed: Boolean(payload?.waiting_for_historical_feed),
            outputCount: Number(payload?.diagnostics?.output_count ?? 0),
            controlCallbackCount: Number(payload?.diagnostics?.control_callback_count ?? 0),
            lastOutputTimestamp: Number.isFinite(Number(payload?.latest_output?.timestamp))
              ? Number(payload.latest_output.timestamp)
              : null,
            runKey: Number.isFinite(Number(startedAt))
              ? `tracking:${Number(startedAt)}`
              : typeof artifactRunKey === 'string' && artifactRunKey
                ? artifactRunKey
                : null,
            batchRunning: Boolean(payload?.energyplus_batch_status?.running),
            batchPhase: String(payload?.energyplus_batch_status?.phase ?? 'idle'),
            batchError: typeof payload?.energyplus_batch_status?.error === 'string'
              ? payload.energyplus_batch_status.error
              : null,
            trackingMode: String(payload?.diagnostics?.tracking_mode ?? 'not_started'),
            phase: String(payload?.diagnostics?.phase ?? 'idle'),
          });
        }
      } catch {
        if (!cancelled) setTrackingMonitor(null);
      }
    };
    void poll();
    const id = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const actions = status.control?.actions;
  const energy = status.energy;
  const policyActive = status.control?.policy_active === true;
  const automaticCalibrationEligible = Boolean(
    status.running
    && status.control?.system_on === true
    && policyActive
    && trackingMonitor?.running
    && (trackingMonitor?.outputCount ?? 0) > 0
    && Number.isFinite(status.frame?.timestamp)
    && Number.isFinite(trackingMonitor?.lastOutputTimestamp)
    && Number(energy?.bems_comparable_kw ?? 0) >= 1
    && Math.abs(
      Number(status.frame?.timestamp) / 1000 - Number(trackingMonitor?.lastOutputTimestamp),
    ) <= AUTOMATIC_CALIBRATION_ALIGNMENT_TOLERANCE_SEC
  );

  useEffect(() => {
    const runKey = trackingMonitor?.runKey;
    if (
      !automaticCalibrationEligible
      || !runKey
      || trackingMonitor?.batchRunning
      || trackingMonitor?.batchPhase === 'done'
      || automaticCalibrationRequestInFlightRef.current
      || automaticCalibrationRunRef.current === runKey
      || !status.frame
    ) return;

    const actions = status.control?.actions;
    const snapshot = status.frame.snapshot;
    const ahuFanKw = Number(energy?.ahu_fan_kw ?? 0);
    const plantKw = Number((energy?.chiller_kw ?? 0) + (energy?.cooling_tower_kw ?? 0));
    const measuredHvacKw = Number(energy?.bems_comparable_kw ?? 0);
    // Match the candidate component schedules to measured operation. Policy
    // commands may be ON even when the corresponding plant is drawing 0 kW.
    const ahuOn = ahuFanKw >= 1;
    const chillerOn = plantKw >= 1;
    const anyEquipmentOn = ahuOn || chillerOn;
    const measuredZoneTemp = averageFinite([
      snapshot['DDC_M.D.D0116'],
      snapshot['DDC_M.D.D0117'],
      snapshot['DDC_M.D.D0118'],
      snapshot['DDC_M.D.D0119'],
    ]);

    automaticCalibrationRunRef.current = runKey;
    automaticCalibrationRequestInFlightRef.current = true;
    setAutomaticCalibrationError(null);
    void fetch('/api/twin/calibration/eplus-batch/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        timestamp: status.frame.timestamp / 1000,
        measured_zone_temp_c: measuredZoneTemp,
        measured_hvac_kw: measuredHvacKw,
        measured_ahu_fans_kw: ahuFanKw,
        measured_chiller_tower_kw: plantKw,
        candidate_mode: 'full_0_1_0_05',
        max_candidates: AUTOMATIC_CALIBRATION_CANDIDATE_COUNT,
        fan_pressure_scales: [1.0],
        expand_fan_pressure_scales: false,
        local_radius: 0.05,
        local_step: 0.05,
        max_parallel_workers: 8,
        timeout_sec: 1200,
        append_to_best_schedule: true,
        operation_schedule_values: componentScheduleForFrame(status.frame.time, anyEquipmentOn),
        ahu_operation_schedule_values: componentScheduleForFrame(status.frame.time, ahuOn),
        plant_operation_schedule_values: componentScheduleForFrame(status.frame.time, chillerOn),
        ahu_sa_temp: actions?.ahu_supply_air_setpoint_c,
        chw_sp: actions?.chilled_water_setpoint_c,
        cw_sp: actions?.condenser_water_setpoint_c,
        bt4_sp: actions?.buffer_tank_4_setpoint_c,
        buffer_tank_sp: 7,
        trigger_source: 'synchronized_test_auto',
      }),
    })
      .then(async response => {
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result?.ok !== true) {
          throw new Error(result?.detail || result?.message || '21개 후보 자동 보정을 시작하지 못했습니다.');
        }
      })
      .catch(error => {
        setAutomaticCalibrationError(
          error instanceof Error ? error.message : '21개 후보 자동 보정 요청에 실패했습니다.',
        );
      })
      .finally(() => {
        automaticCalibrationRequestInFlightRef.current = false;
      });
  }, [
    automaticCalibrationEligible,
    energy?.ahu_fan_kw,
    energy?.bems_comparable_kw,
    energy?.chiller_kw,
    energy?.cooling_tower_kw,
    policyActive,
    status.control?.actions,
    status.control?.system_on,
    status.frame,
    trackingMonitor?.batchRunning,
    trackingMonitor?.batchPhase,
    trackingMonitor?.runKey,
  ]);
  const stageLabel = useMemo(() => {
    if (status.stage === 'running') return '폐루프 실행 중';
    if (status.stage === 'energyplus_warmup') return 'EnergyPlus 준비 중';
    if (status.stage === 'advancing_to_test_window') return '08:00 테스트 구간 준비 중';
    if (status.stage === 'waiting_for_tracking_feedback') return 'Tracking 결과 대기 중';
    if (status.stage === 'loading_policy') return 'AI 정책 로딩 중';
    if (status.stage === 'stopping') return '정지 중';
    if (status.stage === 'stopped') return '테스트 정지';
    if (status.stage === 'offline') return '서버 연결 오류';
    if (status.stage === 'failed') return '실행 오류';
    return status.running ? '시작 중' : '정지';
  }, [status.running, status.stage]);

  const runControl = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setControlError(null);
    try {
      await action();
    } catch (error) {
      setControlError(error instanceof Error ? error.message : '테스트 제어 요청에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const restartTrackingRuntime = async () => {
    const runtimeResponse = await fetch('/api/twin/runtime/restart', { method: 'POST' });
    const runtimeResult = await runtimeResponse.json().catch(() => ({}));
    if (!runtimeResponse.ok || runtimeResult.ok !== true) {
      throw new Error(runtimeResult.error || 'Tracking API 재시작에 실패했습니다.');
    }
  };

  const startSynchronizedTest = async (stepDelaySeconds: number, runMode: TestRunMode = 'demo') => {
    const requestedAt = markTrackingRunRequested();
    try {
      setActiveRunMode(runMode);
      setCalibrationPackageMessage(runMode === 'calibration' ? '자동 보정 실행을 준비하고 있습니다.' : null);
      setCalibrationPackageError(null);
      packagedTrackingRunRef.current = null;
      automaticCalibrationRunRef.current = null;
      automaticCalibrationRequestInFlightRef.current = false;
      setAutomaticCalibrationError(null);
      // A synchronized test is the active virtual BEMS source for every tab.
      // Without this switch EnergyPlus can advance in the background while
      // Twin Analysis and AI Control keep rendering a stale historical frame.
      HistoricalService.stop();
      setPreferredDataSourceMode('energyplus');
      if (EnergyPlusTestbedService.getStatus().running) {
        await EnergyPlusTestbedService.stopSession();
      }
      await restartTrackingRuntime();
      await EnergyPlusTestbedService.restartSession(stepDelaySeconds);
      const energyPlusStatus = await EnergyPlusTestbedService.waitForRunningFrame();
      const startEpoch = energyPlusStatus.frame?.timestamp;
      if (!Number.isFinite(startEpoch)) {
        throw new Error('EnergyPlus 첫 프레임 시각을 확인할 수 없습니다.');
      }
      const actions = energyPlusStatus.control?.actions;
      const startResponse = await fetch('/api/twin/tracking/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tracking_delay_min: stepDelaySeconds / 60,
          start_epoch: Number(startEpoch) / 1000,
          ahu_sa_temp: actions?.ahu_supply_air_setpoint_c ?? 15,
          zone_cooling_sp: 26,
          chw_on: true,
          chw_sp: actions?.chilled_water_setpoint_c ?? 9,
          cw_sp: actions?.condenser_water_setpoint_c ?? 28,
          bt4_sp: actions?.buffer_tank_4_setpoint_c ?? 16,
          buffer_tank_sp: 7,
          run_period_days: runMode === 'calibration' ? 1 : 92,
          best_schedule_timeout_sec: 30,
          require_ready: false,
          allow_system_off_start: true,
          strict_lockstep: true,
          apply_calibration_schedules: runMode === 'calibration',
        }),
      });
      const startResult = await startResponse.json().catch(() => ({}));
      if (!startResponse.ok || startResult.ok !== true) {
        throw new Error(startResult.message || '새 동기 검증 실행을 시작하지 못했습니다.');
      }
      if (typeof startResult.run_key === 'string') markTrackingRunAccepted(startResult.run_key);
    } catch (error) {
      markTrackingRunFailed(requestedAt);
      throw error;
    }
  };

  const startTest = () => runControl(async () => {
    const delay = visibleDemoDelay(status.step_delay_seconds);
    await startSynchronizedTest(delay, 'demo');
  });
  const startCalibration = () => runControl(async () => {
    const delay = visibleDemoDelay(status.step_delay_seconds);
    await startSynchronizedTest(delay, 'calibration');
  });
  const stopTest = () => runControl(async () => {
    await Promise.all([
      EnergyPlusTestbedService.stopSession(),
      fetch('/api/twin/tracking/stop', { method: 'POST' }),
    ]);
  });
  const restartTest = () => runControl(async () => {
    const delay = visibleDemoDelay(status.step_delay_seconds);
    await startSynchronizedTest(delay, activeRunMode);
  });

  const changeSpeed = async (seconds: number) => {
    if (busy || status.step_delay_seconds === seconds) return;
    setBusy(true);
    setControlError(null);
    try {
      if (status.running) {
        await startSynchronizedTest(seconds, activeRunMode);
      } else {
        await EnergyPlusTestbedService.setSpeed(seconds);
      }
    } catch (error) {
      setControlError(error instanceof Error ? error.message : '테스트 속도 변경에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const runGuideAction = (request: EnergyPlusGuideAction | null) => {
      if (!request || !request.id || !['demo', 'calibration'].includes(request.mode)) return;
      if (Date.now() - Number(request.requestedAt || 0) > 30_000) return;
      if (handledGuideActionRef.current === request.id) return;
      handledGuideActionRef.current = request.id;
      try { window.sessionStorage.removeItem(ENERGYPLUS_GUIDE_ACTION_KEY); } catch {}
      void runControl(async () => {
        const delay = visibleDemoDelay(EnergyPlusTestbedService.getStatus().step_delay_seconds);
        await startSynchronizedTest(delay, request.mode);
      });
    };
    const handleEvent = (event: Event) => runGuideAction((event as CustomEvent<EnergyPlusGuideAction>).detail);
    window.addEventListener('aees:energyplus-guide-action', handleEvent);
    let pending: EnergyPlusGuideAction | null = null;
    try {
      pending = JSON.parse(window.sessionStorage.getItem(ENERGYPLUS_GUIDE_ACTION_KEY) || 'null');
    } catch {}
    if (pending) window.setTimeout(() => runGuideAction(pending), 80);
    return () => window.removeEventListener('aees:energyplus-guide-action', handleEvent);
  }, []);

  useEffect(() => {
    const runKey = trackingMonitor?.runKey;
    const calibratedRunComplete = Boolean(
      runKey
      && trackingMonitor?.trackingMode === 'calibrated'
      && !trackingMonitor.running
      && (trackingMonitor.phase === 'complete' || trackingMonitor.requiresProcessRestart)
      && trackingMonitor.outputCount >= 36
    );
    if (!calibratedRunComplete || packagedTrackingRunRef.current === runKey) return;
    packagedTrackingRunRef.current = runKey ?? null;
    setCalibrationPackageMessage('정확도 기준을 확인하고 보정 결과를 저장하고 있습니다.');
    setCalibrationPackageError(null);
    void fetch('/api/operations/calibration-package/build', {
      method: 'POST',
      headers: AuthService.authHeaders(),
    })
      .then(async response => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload.ok !== true) throw new Error(payload.error || payload.detail || '보정 결과 저장 실패');
        if (payload.eligible_for_fine_tuning === true) {
          setCalibrationPackageMessage('보정 완료 · AI 모델 개선에 사용할 검증 결과가 저장되었습니다.');
        } else {
          const failed = Object.entries(payload.gates ?? {})
            .filter(([, passed]) => passed !== true)
            .map(([name]) => name)
            .join(', ');
          setCalibrationPackageMessage(null);
          setCalibrationPackageError(`보정은 완료되었지만 정확도 기준을 다시 확인해야 합니다${failed ? `: ${failed}` : '.'}`);
        }
        window.dispatchEvent(new CustomEvent('aees:operations-readiness-changed'));
      })
      .catch(error => {
        setCalibrationPackageMessage(null);
        setCalibrationPackageError(error instanceof Error ? error.message : '보정 결과를 저장하지 못했습니다.');
        window.dispatchEvent(new CustomEvent('aees:operations-readiness-changed'));
      });
  }, [trackingMonitor]);

  const nextStepAt = status.next_step_at ? Date.parse(status.next_step_at) : NaN;
  const fallbackNextStepAt = status.updated_at && status.stage === 'running'
    ? Date.parse(status.updated_at) + (status.step_delay_seconds ?? 180) * 1000
    : NaN;
  const dueAt = Number.isFinite(nextStepAt) ? nextStepAt : fallbackNextStepAt;
  const remainingSeconds = status.running && Number.isFinite(dueAt)
    ? Math.max(0, Math.ceil((dueAt - now) / 1000))
    : null;
  const remainingLabel = remainingSeconds == null
    ? '--:--'
    : `${String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}:${String(remainingSeconds % 60).padStart(2, '0')}`;
  const trackingLabel = trackingMonitor?.outputStale
    ? trackingMonitor.waitingForFeed
      ? 'Tracking 입력 대기'
      : 'Tracking 동기 오류 · 재시작 필요'
    : trackingMonitor?.running
    ? 'Tracking 실행 중'
    : trackingMonitor?.requiresProcessRestart
      ? 'Tracking 완료 · 재시작 필요'
      : trackingMonitor?.calibrationRunning
        ? '내부발열 보정 중'
        : trackingMonitor?.rlSetpointsLinked
          ? 'Tracking 대기 · AI 연동'
          : 'Tracking 정지';
  const relayLabel = status.tracking_relay?.ok
    ? '서버 릴레이 정상'
    : status.tracking_relay?.disabled
      ? '서버 릴레이 꺼짐'
      : status.tracking_relay?.error
        ? '서버 릴레이 오류'
        : '서버 릴레이 준비 중';
  const feedbackLabel = status.tracking_feedback?.applied
    ? `Tracking→RL 적용 · ${format(status.tracking_feedback.zone_temp_c ?? undefined)}°C`
    : status.tracking_feedback?.status === 'waiting'
      ? 'Tracking→RL 동기화 중'
      : status.tracking_feedback?.status === 'disabled'
        ? 'Tracking→RL 꺼짐'
      : 'Tracking→RL 센서 폴백';
  const trackingOutputTime = trackingMonitor?.lastOutputTimestamp
    ? new Date(trackingMonitor.lastOutputTimestamp * 1000).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })
    : '--:--';
  const trackingProgressLabel = trackingMonitor
    ? `Tracking ${trackingMonitor.outputCount.toLocaleString()}스텝 · 제어 콜백 ${trackingMonitor.controlCallbackCount.toLocaleString()} · 최근 ${trackingOutputTime}`
    : 'Tracking 상태 확인 중';
  const automaticCalibrationLabel = trackingMonitor?.batchRunning
    ? 'Tracking 후보 21개 비교 중'
    : trackingMonitor?.batchPhase === 'done'
      ? 'Tracking 후보 비교 완료'
      : automaticCalibrationError || trackingMonitor?.batchError
        ? '후보 비교 실패 · 트윈 분석에서 재실행 가능'
        : automaticCalibrationEligible
          ? 'Tracking 후보 비교 시작 중'
          : '시스템 ON·Tracking 동기 후 후보 자동 비교';

  const setPanelMinimized = (value: boolean) => {
    setMinimized(value);
    try {
      window.localStorage.setItem('aees:energyplus-panel-minimized', value ? '1' : '0');
    } catch {
      // localStorage may be unavailable in restricted browser contexts.
    }
  };

  const isReporting = activeTab === 'reporting';
  const showCompactPanel = minimized || (isReporting && !reportPanelExpanded);

  useEffect(() => {
    if (!isReporting) setReportPanelExpanded(false);
  }, [isReporting]);

  if (showCompactPanel) {
    return (
      <aside
        className="energyplus-testbed-panel fixed bottom-4 left-1/2 z-[80] w-[min(680px,calc(100vw-2rem))] -translate-x-1/2 rounded-2xl border border-[#bdd9da] bg-white/96 px-3 py-2.5 shadow-[0_14px_38px_rgba(15,35,45,0.16)] backdrop-blur-xl"
        data-report-compact={isReporting ? 'true' : 'false'}
      >
        <div className="flex items-center gap-3 text-[11px]">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${status.running ? 'bg-[#22a06b] animate-pulse' : 'bg-[#c05b62]'}`} />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <p className="truncate font-bold text-[#08777d]">EnergyPlus·Tracking 테스트</p>
              <span className="shrink-0 text-[#8a9298]">·</span>
              {activeRunMode === 'calibration' && (
                <span className="shrink-0 rounded-full bg-[#e7f5f3] px-2 py-0.5 text-[9px] font-bold text-[#08777d]">자동 보정</span>
              )}
              <p className="truncate font-semibold text-[#59616a]">{stageLabel}</p>
            </div>
            <p className="mt-0.5 truncate text-[10px] text-[#7a8288]">
              {status.frame ? `${status.frame.date} ${status.frame.time.slice(0, 5)}` : '시뮬레이션 준비 중'} · {trackingLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (minimized) setPanelMinimized(false);
              if (isReporting) setReportPanelExpanded(true);
            }}
            className="flex h-9 shrink-0 items-center gap-1.5 rounded-xl border border-[#dce5e5] bg-[#f7f9f9] px-3 font-semibold text-[#08777d] transition hover:bg-[#edf7f7]"
            title={isReporting ? '운영 리포트 위에서 EnergyPlus 보정 패널 펼치기' : 'EnergyPlus 보정 패널 펼치기'}
          >
            <i className="fa-solid fa-chevron-up" />
            <span className="hidden sm:inline">펼치기</span>
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="energyplus-testbed-panel fixed bottom-4 left-1/2 z-[80] max-h-[calc(100vh-2rem)] w-[min(1320px,calc(100vw-2rem))] -translate-x-1/2 overflow-y-auto rounded-2xl border border-[#bdd9da] bg-white/95 px-4 py-3 shadow-[0_18px_50px_rgba(15,35,45,0.18)] backdrop-blur-xl"
      data-expanded={expanded}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-[#59616a]">
        <div className="flex min-w-[180px] items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${status.running ? 'bg-[#22a06b] animate-pulse' : 'bg-[#c05b62]'}`} />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-bold text-[#08777d]">EnergyPlus·Tracking 테스트</p>
              {activeRunMode === 'calibration' && (
                <span className="rounded-full bg-[#e7f5f3] px-2 py-0.5 text-[9px] font-bold text-[#08777d]">자동 보정</span>
              )}
            </div>
            <p>{stageLabel} · 서버 8003</p>
          </div>
        </div>
        <div className="border-l border-[#dfe7e7] pl-4">
          <p className="font-semibold text-[#171a20]">{status.frame ? `${status.frame.date} ${status.frame.time.slice(0, 5)}` : '시뮬레이션 준비 중'}</p>
          <p>스텝 #{status.step_index ?? '--'} · 다음 스텝 {remainingLabel}</p>
        </div>
        <div className={`${expanded ? 'flex' : 'hidden'} flex-1 flex-wrap gap-1.5 lg:flex`}>
          {policyActive ? (
            <>
              <span className="rounded-full bg-[#edf7f7] px-2.5 py-1 text-[#08777d]">AHU SA {format(actions?.ahu_supply_air_setpoint_c)}°C</span>
              <span className="rounded-full bg-[#edf7f7] px-2.5 py-1 text-[#08777d]">CHW {format(actions?.chilled_water_setpoint_c)}°C</span>
              <span className="rounded-full bg-[#edf7f7] px-2.5 py-1 text-[#08777d]">CW {format(actions?.condenser_water_setpoint_c)}°C</span>
              <span className="rounded-full bg-[#edf7f7] px-2.5 py-1 text-[#08777d]">BT4 {format(actions?.buffer_tank_4_setpoint_c)}°C</span>
              <span className={`rounded-full px-2.5 py-1 font-semibold ${actions?.chiller_on ? 'bg-[#e8f7ef] text-[#167452]' : 'bg-[#f1f3f4] text-[#777d85]'}`}>
                냉동기 명령 {actions?.chiller_on ? 'ON' : 'OFF'}
              </span>
            </>
          ) : (
            <span className="rounded-full border border-[#d9dee2] bg-[#f5f6f7] px-3 py-1 font-semibold text-[#6f767d]">
              시스템 OFF · RL 추론 및 설정온도 제어 비활성
            </span>
          )}
        </div>
        <div className={`${expanded ? 'block' : 'hidden'} min-w-[205px] text-right sm:block`}>
          <p className="font-semibold text-[#171a20]">비교 전력 {format(energy?.bems_comparable_kw)} kW</p>
          <p>냉동기 {format(energy?.chiller_kw)} · 냉각탑 {format(energy?.cooling_tower_kw)} · AHU {format(energy?.ahu_fan_kw)}</p>
        </div>
        <div className={`${expanded ? 'flex' : 'hidden'} items-center gap-1 rounded-xl bg-[#f1f6f6] p-1 xl:flex`} aria-label="타임스텝 대기시간">
          <span className="px-1.5 font-semibold text-[#08777d]">1스텝</span>
          {SPEED_OPTIONS.map(option => {
            const selected = Math.round(status.step_delay_seconds ?? 180) === option.seconds;
            return (
              <button
                key={option.seconds}
                type="button"
                disabled={busy}
                aria-pressed={selected}
                onClick={() => changeSpeed(option.seconds)}
                className={`rounded-lg px-2.5 py-1.5 font-semibold transition ${selected
                  ? 'bg-[#08777d] text-white shadow-sm'
                  : 'text-[#65717a] hover:bg-white hover:text-[#08777d]'
                } disabled:cursor-wait disabled:opacity-50`}
              >
                {option.label}{'recommended' in option && option.recommended ? ' 시연' : ''}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5 rounded-xl border border-[#dce5e5] bg-white p-1">
          <button
            type="button"
            disabled={busy || status.running}
            onClick={startTest}
            className="rounded-lg bg-[#08777d] px-3 py-2 font-semibold text-white transition hover:bg-[#066a6f] disabled:cursor-not-allowed disabled:bg-[#c7d5d5]"
          >
            <i className="fa-solid fa-play mr-1.5" />테스트 시작
          </button>
          <button
            type="button"
            disabled={busy || status.running}
            onClick={startCalibration}
            className="rounded-lg border border-[#80bbb6] bg-[#f1faf8] px-3 py-2 font-semibold text-[#08777d] transition hover:bg-[#e3f4f1] disabled:cursor-not-allowed disabled:opacity-35"
            title="하루 분량 Tracking 보정 후 검증 결과를 자동 저장합니다."
          >
            <i className="fa-solid fa-sliders mr-1.5" />자동 보정
          </button>
          <button
            type="button"
            disabled={busy || !status.running}
            onClick={stopTest}
            className="rounded-lg border border-[#e0b7b9] px-3 py-2 font-semibold text-[#a7424b] transition hover:bg-[#fff3f3] disabled:cursor-not-allowed disabled:opacity-35"
          >
            <i className="fa-solid fa-stop mr-1.5" />테스트 정지
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={restartTest}
            className="rounded-lg border border-[#bad7d8] px-3 py-2 font-semibold text-[#08777d] transition hover:bg-[#edf7f7] disabled:cursor-wait disabled:opacity-50"
          >
            <i className="fa-solid fa-rotate-left mr-1.5" />처음부터
          </button>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded(value => !value)}
            className="rounded-lg border border-[#dce5e5] px-3 py-2 font-semibold text-[#59616a] transition hover:bg-[#f1f6f6] lg:hidden"
          >
            <i className={`fa-solid ${expanded ? 'fa-chevron-up' : 'fa-sliders'} mr-1.5`} />
            {expanded ? '간단히' : '상세'}
          </button>
          <button
            type="button"
            onClick={() => {
              if (isReporting) setReportPanelExpanded(false);
              else setPanelMinimized(true);
            }}
            className="rounded-lg border border-[#dce5e5] px-3 py-2 font-semibold text-[#59616a] transition hover:bg-[#f1f6f6]"
            title="EnergyPlus 보정 패널 최소화"
          >
            <i className="fa-solid fa-chevron-down mr-1.5" />접기
          </button>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between border-t border-[#e7eded] pt-2 text-[10px]">
        <div className="min-w-0">
          <p className={trackingMonitor?.running || trackingMonitor?.rlSetpointsLinked ? 'truncate font-semibold text-[#08777d]' : 'truncate text-[#7a8288]'}>
            <i className="fa-solid fa-wave-square mr-1.5" />{trackingLabel} · {trackingProgressLabel} · {relayLabel} · {feedbackLabel}
          </p>
          <p className="mt-0.5 text-[#8a9298]">{automaticCalibrationLabel}</p>
          {calibrationPackageMessage && <p className="mt-0.5 font-semibold text-[#247565]">{calibrationPackageMessage}</p>}
          {calibrationPackageError && <p className="mt-0.5 font-semibold text-[#a76122]">{calibrationPackageError}</p>}
        </div>
        <p className="shrink-0 text-[#727a80]">EnergyPlus 20분 = 실제 {durationLabel(status.step_delay_seconds ?? 180)}</p>
      </div>
      {controlError || automaticCalibrationError || status.stage === 'offline' || status.stage === 'failed' ? (
        <p className="mt-2 border-t border-[#f0d7d9] pt-2 text-[11px] text-[#b33f51]">{controlError ?? automaticCalibrationError ?? status.message}</p>
      ) : null}
    </aside>
  );
};
